import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader';
import { safeWindowOpen } from '~/helpers/safeUrlOpen.ts';
import { withBlobContentType } from '~/helpers/utils.ts';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import {
  GUARANTEES_APPLICATION_AM_REVIEW_MODE,
  GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM,
  GUARANTEES_APPLICATION_MANUAL_TASK_QUERY_PARAM,
  GUARANTEES_APPLICATION_REVIEW_MODE_QUERY_PARAM,
  guaranteesApplicationStatusConfig,
  guaranteesClientText,
} from '~/modules/guarantees/form/client/constants.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import {
  getStringValue,
  isManualAmApprovalLetterInProgress,
} from '~/modules/guarantees/utils.ts';
import {
  selectGuaranteesAdministrationRoles,
  selectGuaranteesAdministrationRolesStatus,
  selectGuaranteesCurrentApplication,
  selectGuaranteesCurrentApplicationError,
  selectGuaranteesCurrentApplicationStatus,
  selectGuaranteesManagerSupervisorInfoStatus,
  selectHasGuaranteesAmAccess,
  selectGuaranteesSubmitApplicationByUdbtfError,
  selectGuaranteesSubmitApplicationByUdbtfStatus,
  selectGuaranteesUdbtfTemplateDocument,
  selectGuaranteesUdbtfTemplateDocumentError,
  selectGuaranteesUdbtfTemplateDocumentStatus,
} from '~/store/guarantees/selectors.ts';
import {
  fetchGuaranteesAdministrationRoles,
  fetchGuaranteesApplication,
  fetchGuaranteesUdbtfTemplateDocument,
  submitGuaranteesApplicationByUdbtf,
} from '~/store/guarantees/thunks/applicationThunks.ts';
import {
  AdministrationRole,
  GuaranteesApplicationStatus,
  GuaranteesFetchStatus,
} from '~/store/guarantees/types.ts';
import { normalizeGuaranteesApplicationStatus } from '~/store/guarantees/utils.ts';
import type { AppDispatch } from '~/store/store.ts';
import FlowSection from '../../components/root/shared/FlowSection.tsx';
import ApplicationHeader from '../../shared/components/ApplicationHeader.tsx';
import ApplicationPageFrame from '../../shared/components/ApplicationPageFrame.tsx';
import { useApplicationRequestFeedback } from '../../shared/hooks/useApplicationRequestFeedback.ts';
import { useFreshGuaranteesRequestError } from '../../shared/hooks/useFreshGuaranteesRequestError.ts';
import { getCurrentRouteGuaranteesApplication } from '../../shared/utils/applicationIdentity.ts';
import { isGuaranteesApprovalLetterPaperExecutionCheck } from '../../shared/approvalLetterExecution.ts';
import ButtonLoadingIcon from '../../../shared/ButtonLoadingIcon.tsx';
import GuaranteesManagerApplicationSidePanelLayout from '../shared/SidePanel/GuaranteesManagerApplicationSidePanelLayout.tsx';
import GuaranteesManagerStepNavigator from '../stepNavigation/GuaranteesManagerStepNavigator.tsx';
import {
  MANAGER_STEP3_ACCESS_DENIED_TEXT,
  MANAGER_STEP3_LOADING_TEXT,
  MANAGER_STEP3_MISSING_PDF_TEXT,
  MANAGER_STEP3_TEXT,
} from './constants.ts';

const ManagerStep3Page: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId = '' } = useParams();
  const contragentId =
    new URLSearchParams(location.search).get(GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM) ?? '';
  const isAmReviewMode =
    new URLSearchParams(location.search).get(GUARANTEES_APPLICATION_REVIEW_MODE_QUERY_PARAM) ===
    GUARANTEES_APPLICATION_AM_REVIEW_MODE;
  const isManualTaskMode =
    new URLSearchParams(location.search).get(GUARANTEES_APPLICATION_MANUAL_TASK_QUERY_PARAM) ===
    'true';
  const application = useSelector(selectGuaranteesCurrentApplication);
  const applicationStatus = useSelector(selectGuaranteesCurrentApplicationStatus);
  const applicationError = useSelector(selectGuaranteesCurrentApplicationError);
  const administrationRoles = useSelector(selectGuaranteesAdministrationRoles);
  const administrationRolesStatus = useSelector(selectGuaranteesAdministrationRolesStatus);
  const hasUdbtfAccess = administrationRoles.includes(AdministrationRole.UBTF_ADMINISTRATOR);
  const managerSupervisorInfoStatus = useSelector(selectGuaranteesManagerSupervisorInfoStatus);
  const hasAmAccess = useSelector(selectHasGuaranteesAmAccess);
  const udbtfTemplateDocument = useSelector(selectGuaranteesUdbtfTemplateDocument);
  const udbtfTemplateDocumentStatus = useSelector(selectGuaranteesUdbtfTemplateDocumentStatus);
  const udbtfTemplateDocumentError = useSelector(selectGuaranteesUdbtfTemplateDocumentError);
  const submitApplicationByUdbtfStatus = useSelector(
    selectGuaranteesSubmitApplicationByUdbtfStatus
  );
  const submitApplicationByUdbtfError = useSelector(selectGuaranteesSubmitApplicationByUdbtfError);
  const [pdfUrl, setPdfUrl] = React.useState('');
  const { currentRouteApplication, isLoadedApplicationCurrent } =
    getCurrentRouteGuaranteesApplication(application, applicationId);
  const redirectPath = !contragentId ? '/guarantees/administrator/get-started' : '';
  const effectiveApplicationId =
    getStringValue(currentRouteApplication?.applicationId || currentRouteApplication?.id) ||
    applicationId;
  const applicationNumber =
    getStringValue(currentRouteApplication?.applicationNumber) ||
    getStringValue(currentRouteApplication?.root?.applicationNumber) ||
    effectiveApplicationId;
  const normalizedStatus = normalizeGuaranteesApplicationStatus(
    currentRouteApplication?.applicationStatus
  );
  const canEditManualAmApprovalLetterInProgress =
    isAmReviewMode && isManualAmApprovalLetterInProgress(currentRouteApplication, isManualTaskMode);
  const isRestrictedAmReviewMode = isAmReviewMode && !canEditManualAmApprovalLetterInProgress;
  const isPaperApplication = isGuaranteesApprovalLetterPaperExecutionCheck(
    currentRouteApplication
  );
  const statusConfig =
    guaranteesApplicationStatusConfig[normalizedStatus] ??
    guaranteesApplicationStatusConfig[GuaranteesApplicationStatus.DRAFT];
  const isApplicationLoading =
    (applicationStatus === GuaranteesFetchStatus.Loading && !currentRouteApplication) ||
    (!!application && !isLoadedApplicationCurrent);
  const isUdbtfAccessLoading =
    isAmReviewMode
      ? managerSupervisorInfoStatus === GuaranteesFetchStatus.Idle ||
        managerSupervisorInfoStatus === GuaranteesFetchStatus.Loading
      : administrationRolesStatus === GuaranteesFetchStatus.Idle ||
        administrationRolesStatus === GuaranteesFetchStatus.Loading;
  const isUdbtfAccessDenied =
    !isUdbtfAccessLoading && (isAmReviewMode ? !hasAmAccess : !hasUdbtfAccess);
  const {
    modalAlert,
    modalSuccess,
    setModalAlert,
    setModalSuccess,
    showGuaranteesRequestError,
    handleCloseAlertModal,
    handleCloseSuccessModal,
  } = useApplicationRequestFeedback({
    applicationId,
    contragentId,
    dispatch,
  });
  const headerProps = useMemo(
    () => ({
      applicationNumber,
      applicationNumberLabel: guaranteesClientText.applicationsNumberLabel,
      applicationIdLabel: guaranteesClientText.applicationIdLabel,
      backLabel: guaranteesClientText.backToApplicationsButton,
      contragentId,
      contragentIdLabel: guaranteesClientText.contragentIdLabel,
      effectiveApplicationId,
      onBack: () => navigate('/tasks'),
      statusConfig,
    }),
    [applicationNumber, contragentId, effectiveApplicationId, navigate, statusConfig]
  );
  const isDocumentLoading = udbtfTemplateDocumentStatus === GuaranteesFetchStatus.Loading;
  const isSubmittingByUdbtf =
    submitApplicationByUdbtfStatus === GuaranteesFetchStatus.Loading;
  const currentApplicationVersion =
    typeof currentRouteApplication?.version === 'number'
      ? currentRouteApplication.version
      : undefined;
  const isSubmitApplicationDisabled =
    currentApplicationVersion === undefined || isSubmittingByUdbtf || isApplicationLoading;
  const submitApplicationStartIcon = isSubmittingByUdbtf ? <ButtonLoadingIcon /> : undefined;
  const handleOpenPdf = useCallback(() => {
    if (!pdfUrl) {
      setModalAlert({ text: MANAGER_STEP3_MISSING_PDF_TEXT });

      return;
    }

    safeWindowOpen(pdfUrl, { base: window.location.origin });
  }, [pdfUrl, setModalAlert]);

  const handleCloseUdbtfAccessModal = useCallback(() => {
    handleCloseAlertModal();
    navigate('/tasks', { replace: true });
  }, [handleCloseAlertModal, navigate]);

  const navigateToTasks = useCallback(() => {
    navigate('/tasks', { replace: true });
  }, [navigate]);

  const handleSubmitByUdbtf = useCallback(async () => {
    if (!effectiveApplicationId || currentApplicationVersion === undefined) {
      return;
    }

    try {
      await dispatch(
        submitGuaranteesApplicationByUdbtf({
          applicationId: effectiveApplicationId,
          version: currentApplicationVersion,
        })
      ).unwrap();
      setModalSuccess({
        text: MANAGER_STEP3_TEXT.submitSuccessMessage,
        buttonValue: MANAGER_STEP3_TEXT.successCloseButton,
        onClick: navigateToTasks,
        onClose: navigateToTasks,
      });
    } catch (error) {
      showGuaranteesRequestError(error);
    }
  }, [
    currentApplicationVersion,
    dispatch,
    effectiveApplicationId,
    navigateToTasks,
    setModalSuccess,
    showGuaranteesRequestError,
  ]);

  useEffect(() => {
    if (isAmReviewMode || administrationRolesStatus !== GuaranteesFetchStatus.Idle) {
      return;
    }

    void dispatch(fetchGuaranteesAdministrationRoles());
  }, [administrationRolesStatus, dispatch, isAmReviewMode]);

  useEffect(() => {
    if (!contragentId || !applicationId || applicationId === 'new') {
      return;
    }

    void dispatch(fetchGuaranteesApplication({ contragentId, applicationId }));
    void dispatch(fetchGuaranteesUdbtfTemplateDocument({ applicationId }));
  }, [applicationId, contragentId, dispatch]);

  useEffect(() => {
    if (!(udbtfTemplateDocument?.data instanceof Blob)) {
      setPdfUrl('');

      return;
    }

    const previewBlob = withBlobContentType(
      udbtfTemplateDocument.data,
      udbtfTemplateDocument.contentType
    );
    const nextPdfUrl = URL.createObjectURL(previewBlob);

    setPdfUrl(nextPdfUrl);

    return () => {
      URL.revokeObjectURL(nextPdfUrl);
    };
  }, [udbtfTemplateDocument]);

  useEffect(() => {
    if (
      udbtfTemplateDocumentStatus === GuaranteesFetchStatus.Success &&
      !(udbtfTemplateDocument?.data instanceof Blob)
    ) {
      setModalAlert({ text: MANAGER_STEP3_MISSING_PDF_TEXT });
    }
  }, [setModalAlert, udbtfTemplateDocument?.data, udbtfTemplateDocumentStatus]);

  useFreshGuaranteesRequestError(
    applicationError || udbtfTemplateDocumentError || submitApplicationByUdbtfError,
    showGuaranteesRequestError
  );

  useEffect(() => {
    if (isUdbtfAccessDenied) {
      setModalAlert({ text: MANAGER_STEP3_ACCESS_DENIED_TEXT });
    }
  }, [isUdbtfAccessDenied, setModalAlert]);

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  if (isUdbtfAccessLoading) {
    return <FullPageLoader />;
  }

  return (
    <ApplicationPageFrame
      modalAlert={modalAlert}
      modalSuccess={modalSuccess}
      onModalClose={isUdbtfAccessDenied ? handleCloseUdbtfAccessModal : handleCloseAlertModal}
      onSuccessModalClose={handleCloseSuccessModal}
    >
      <ApplicationHeader {...headerProps} />

      {isUdbtfAccessDenied ? null : isApplicationLoading ? (
        <Box sx={commonStyles.centeredLoader}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={guaranteesClientPageStyles.applicationFlowStack}>
          <GuaranteesManagerApplicationSidePanelLayout
            applicationId={effectiveApplicationId}
            onError={showGuaranteesRequestError}
          >
            <FlowSection
              step={MANAGER_STEP3_TEXT.sectionStep}
              title={MANAGER_STEP3_TEXT.sectionTitle}
              description={MANAGER_STEP3_TEXT.sectionDescription}
            >
              <Box sx={guaranteesClientPageStyles.generatedDocumentPreview}>
                {isDocumentLoading ? (
                  <Box sx={guaranteesClientPageStyles.flowLoadingState}>
                    <CircularProgress size={24} />
                    {MANAGER_STEP3_LOADING_TEXT}
                  </Box>
                ) : (
                  <Box
                    component="iframe"
                    src={pdfUrl || undefined}
                    title={MANAGER_STEP3_TEXT.previewTitle}
                    sx={guaranteesClientPageStyles.generatedDocumentIframe}
                  />
                )}

                <Box sx={guaranteesClientPageStyles.generatedDocumentActions}>
                  <Button
                    variant="outlined"
                    color="primary"
                    type="button"
                    sx={guaranteesClientPageStyles.generatedDocumentOpenButton}
                    disabled={!pdfUrl}
                    onClick={handleOpenPdf}
                  >
                    {MANAGER_STEP3_TEXT.openButton}
                  </Button>
                  {!isRestrictedAmReviewMode && !isPaperApplication && (
                    <Button
                      variant="contained"
                      color="primary"
                      type="button"
                      sx={guaranteesClientPageStyles.generatedDocumentOpenButton}
                      disabled={isSubmitApplicationDisabled}
                      onClick={() => void handleSubmitByUdbtf()}
                      startIcon={submitApplicationStartIcon}
                    >
                      {MANAGER_STEP3_TEXT.submitButton}
                    </Button>
                  )}
                </Box>
              </Box>
            </FlowSection>
          </GuaranteesManagerApplicationSidePanelLayout>

          <GuaranteesManagerStepNavigator
            application={currentRouteApplication}
            applicationId={effectiveApplicationId}
            contragentId={contragentId}
            currentStepKey="udbtf"
          />
        </Box>
      )}
    </ApplicationPageFrame>
  );
};

export default ManagerStep3Page;
